import type { FC, ReactNode } from 'react';
import { lazy } from 'react';
import { useTranslation } from 'react-i18next';

import { MessageBox } from '@ifs-gcp/components';
import type { IConditionHandler, ITemplateNode } from '@ifs-gcp/core';
import {
  isAuditResultNode,
  isCardNode,
  isChecklistNode,
  isCompanyGpsCoordinatesWidgetNode,
  isDocumentUploadNode,
  isHintModalNode,
  isMessageBoxNode,
  isPackagingScopeNode,
  isProductScopeNode,
  isQuestionNode,
  isQuestionTextNode,
  isReportNode,
  isScoringMatrixNode,
  isStatusNode,
  isTabCardNode,
  isTextNode,
} from '@ifs-gcp/core/helpers';
import type { BaseTemplateElement } from '@ifs-gcp/core/models';

import {
  AuditQuestionElement,
  AuditTitle,
  Card,
  Checklist,
  Report,
  TabCard,
} from '@/components/audit';
import { WidgetDocuments } from '@/components/audit/documents';
import { WidgetPackagingScopes } from '@/components/audit/packaging-scopes';
import { WidgetProductScopes } from '@/components/audit/product-scopes';
import { WidgetResultMatrix } from '@/components/audit/result-matrix/result-matrix';
import { WidgetAuditResult } from '@/components/audit/result/result';
import { StatusTable } from '@/components/audit/status/status';
import { WidgetCompanyGpsCoordinates } from '@/components/audit/widgets';
import { QuestionText } from '@/components/question/question-text';
import { useAuditStore } from '@/store/auditStore';

const LazyHintModal = lazy(() => import('@/components/hint-modal/hint-modal'));

export interface FactoryElementProps {
  element: ITemplateNode<BaseTemplateElement>;
}

type RenderFn = (
  element: ITemplateNode<BaseTemplateElement>,
  FactoryElement: FC<FactoryElementProps>,
  language: string,
  conditions: IConditionHandler,
) => ReactNode | null;

const ELEMENT_COMPONENT_MAP: RenderFn[] = [
  (element, _, language, conditions) =>
    isQuestionTextNode(element) ? (
      <QuestionText element={element} language={language} conditions={conditions} />
    ) : null,
  (element, FactoryElement) =>
    isQuestionNode(element) ? (
      <AuditQuestionElement element={element} FactoryElement={FactoryElement} />
    ) : null,
  (element) => (isTextNode(element) ? <AuditTitle element={element} /> : null),
  (element, FactoryElement) =>
    isCompanyGpsCoordinatesWidgetNode(element) ? (
      <WidgetCompanyGpsCoordinates element={element} FactoryElement={FactoryElement} />
    ) : null,
  (element) => (isAuditResultNode(element) ? <WidgetAuditResult element={element} /> : null),
  (element) => (isScoringMatrixNode(element) ? <WidgetResultMatrix element={element} /> : null),
  (element, FactoryElement) =>
    isTabCardNode(element) ? <TabCard element={element} FactoryElement={FactoryElement} /> : null,
  (element, FactoryElement, language) =>
    isCardNode(element) ? (
      <Card element={element} language={language} FactoryElement={FactoryElement} />
    ) : null,
  (element) => (isChecklistNode(element) ? <Checklist element={element} /> : null),
  (element) => (isReportNode(element) ? <Report element={element} /> : null),
  (element) => (isStatusNode(element) ? <StatusTable moduleId={element.props.context[0]} /> : null),
  (element) => (isProductScopeNode(element) ? <WidgetProductScopes element={element} /> : null),
  (element) => (isPackagingScopeNode(element) ? <WidgetPackagingScopes element={element} /> : null),
  (element) => (isDocumentUploadNode(element) ? <WidgetDocuments element={element} /> : null),
  (element, _, language) =>
    isMessageBoxNode(element) ? <MessageBox element={element} language={language} /> : null,
  (element) =>
    isHintModalNode(element) ? <LazyHintModal key={element.props.id} element={element} /> : null,
];

export const AuditFactoryElement = ({ element }: FactoryElementProps) => {
  const {
    i18n: { language },
  } = useTranslation();

  const audit = useAuditStore((state) => state.audit);

  if (!audit) return null;

  for (const renderFn of ELEMENT_COMPONENT_MAP) {
    const component = renderFn(element, AuditFactoryElement, language, audit.conditions);

    if (component) return component;
  }

  console.log('Missing element type component for', element.type);

  return null;
};
